import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import StyleCard from "./components/StyleCard";
import StyleDetailPanel from "./components/StyleDetailPanel";
import {
  conservatoryStyles,
  conservatoryCategories,
  type ConservatoryStyle,
} from "../../mocks/conservatories";

const faqItems = [
  {
    q: "Do I need planning permission for a conservatory in Surrey?",
    a: "Most conservatories fall within Permitted Development rights and do not require planning permission, provided they meet certain criteria — typically under 50% of the garden footprint, under 4m high for a pitched roof, and not built on the front or side elevation. We advise on this during your free survey and handle any applications where required.",
  },
  {
    q: "How much does a conservatory cost in Caterham?",
    a: "Conservatory prices vary significantly depending on the style, size, specification, and any groundworks required. A standard 3m x 3m lean-to starts from around £7,500 fully installed, while a premium orangery or bespoke design can reach £50,000+. We provide detailed, transparent written quotations after a free home survey.",
  },
  {
    q: "Can I use a conservatory all year round?",
    a: "Modern conservatories with solar-control glazing, thermally-broken frames, and well-designed ventilation are significantly more comfortable year-round than older designs. For the best year-round performance, we recommend an orangery-style solid perimeter roof with a glazed lantern, or a hybrid solid/glass roof design. We will advise during your survey.",
  },
  {
    q: "How long does it take to build a conservatory?",
    a: "Construction typically takes 2–4 weeks once the base is prepared. The full timeline from first survey to completion is typically 8–12 weeks, depending on groundwork requirements, manufacturing lead times, and planning. We keep you informed at every stage.",
  },
  {
    q: "What glazing should I choose for my conservatory?",
    a: "We recommend self-cleaning solar-control glass as standard, which reflects solar heat in summer and retains warmth in winter. For roofs, we offer polycarbonate (affordable), glass (premium look), and solid tiled systems (best insulation). We will walk you through the options during your free survey.",
  },
  {
    q: "Can you build on an existing conservatory base?",
    a: "In many cases yes — if the existing base slab and dwarf walls are structurally sound, we can build your new conservatory on the existing footprint. This can significantly reduce cost and disruption. We always inspect the base as part of our free survey.",
  },
];

const processSteps = [
  { step: "1", title: "Free Home Survey", desc: "We visit your home, measure up, assess your garden and elevation, and discuss every option available." },
  { step: "2", title: "Design & Quotation", desc: "We produce a detailed design proposal with clear, transparent pricing and no hidden extras." },
  { step: "3", title: "Manufacture & Base", desc: "Your conservatory components are manufactured to order while our team prepares the base and any groundworks." },
  { step: "4", title: "Installation", desc: "Our experienced team builds your conservatory from ground to ridge, leaving your garden clean and tidy on completion." },
];

const testimonials = [
  {
    name: "Karen & Steven Bright",
    location: "Caterham, Surrey",
    text: "Our Victorian conservatory has absolutely transformed the back of the house. It is genuinely the most-used room in the home now — summer and winter. The team were meticulous from start to finish.",
    avatar: "KB",
    style: "Victorian",
  },
  {
    name: "James Whitmore",
    location: "Warlingham, Surrey",
    text: "I went for an orangery after spending weeks looking at options. The difference compared to our old lean-to is incredible — it genuinely feels like part of the house rather than a glass box. Highly recommend.",
    avatar: "JW",
    style: "Orangery",
  },
  {
    name: "The Okafor Family",
    location: "Purley, South London",
    text: "We had an Edwardian 4m x 3.5m installed. The floor space is brilliant — we have a dining table at one end and a sofa at the other. Ensure Windows were professional, communicative, and delivered exactly what they promised.",
    avatar: "OF",
    style: "Edwardian",
  },
];

export default function ConservatoriesPage() {
  const [activeCategory, setActiveCategory] = useState("all");
  const [selectedStyle, setSelectedStyle] = useState<ConservatoryStyle | null>(null);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [quoteStyle, setQuoteStyle] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", style: "", size: "", message: "" });
  const quoteRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    document.title = "Conservatories Caterham Surrey | All Styles & Pricing | Ensure Windows";
    return () => { document.title = "Ensure Windows Caterham | Premium Windows & Doors Surrey"; };
  }, []);

  const filtered = activeCategory === "all"
    ? conservatoryStyles
    : conservatoryStyles.filter((s) => s.category === activeCategory);

  const handleSelectStyle = (style: ConservatoryStyle) => {
    setSelectedStyle(prev => prev?.id === style.id ? null : style);
  };

  const handleQuoteForStyle = (styleName: string) => {
    setQuoteStyle(styleName);
    setForm(prev => ({ ...prev, style: styleName }));
    setTimeout(() => quoteRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await fetch("https://readdy.ai/api/form/d722nqeuimmp87kg1rtg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(form as Record<string, string>).toString(),
      });
    } finally {
      setSubmitted(true);
      setSubmitting(false);
    }
  };

  return (
    <main>
      <Navbar />

      {/* Hero */}
      <section className="relative min-h-[88vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/0f7960f8d80b14c8b1046af4c9c20496.jpg"
            alt="Conservatories Surrey Caterham"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/55 to-transparent" />
        </div>
        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 pt-24">
          <div className="max-w-2xl">
            <nav className="flex items-center gap-2 text-xs text-stone-400 mb-6" aria-label="Breadcrumb" style={{ fontFamily: "'Raleway', sans-serif" }}>
              <Link to="/" className="hover:text-amber-400 transition-colors cursor-pointer">Home</Link>
              <i className="ri-arrow-right-s-line" />
              <Link to="/products" className="hover:text-amber-400 transition-colors cursor-pointer">Products</Link>
              <i className="ri-arrow-right-s-line" />
              <span className="text-white">Conservatories</span>
            </nav>
            <span className="inline-block text-amber-400 text-xs font-bold tracking-widest uppercase border border-amber-400/50 px-4 py-1.5 rounded-full mb-5" style={{ fontFamily: "'Raleway', sans-serif" }}>
              Caterham · Surrey · South London
            </span>
            <h1 className="text-5xl lg:text-6xl xl:text-7xl font-bold text-white leading-tight mb-5" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Conservatories &amp; Orangeries in Surrey
            </h1>
            <p className="text-lg text-stone-200 leading-relaxed mb-8" style={{ fontFamily: "'Raleway', sans-serif" }}>
              Browse all 18 conservatory styles — from classic Victorian to bespoke orangeries — with transparent pricing guidance and expert installation across Caterham, Surrey, and South London.
            </p>
            <div className="flex flex-wrap gap-4">
              <a href="#styles" className="bg-amber-600 hover:bg-amber-500 text-white font-semibold px-8 py-4 rounded-md transition-colors whitespace-nowrap cursor-pointer" style={{ fontFamily: "'Raleway', sans-serif" }}>
                Browse All Styles
              </a>
              <a href="#quote-form" className="border border-white/60 hover:border-white text-white hover:bg-white/10 font-semibold px-8 py-4 rounded-md transition-all whitespace-nowrap cursor-pointer" style={{ fontFamily: "'Raleway', sans-serif" }}>
                Get a Free Quote
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Stats bar */}
      <section className="bg-zinc-900 py-7">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-4 divide-x divide-white/10">
          {[
            { icon: "ri-home-4-line", v: "18", l: "Conservatory Styles" },
            { icon: "ri-award-line", v: "10 Yr", l: "Installation Guarantee" },
            { icon: "ri-sun-line", v: "A-Rated", l: "Solar Control Glass" },
            { icon: "ri-calendar-check-line", v: "Free", l: "Home Survey" },
          ].map((b) => (
            <div key={b.l} className="flex flex-col items-center text-center px-4">
              <div className="w-7 h-7 flex items-center justify-center mb-2"><i className={`${b.icon} text-amber-400 text-lg`} /></div>
              <span className="text-white text-xl font-bold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{b.v}</span>
              <span className="text-stone-400 text-xs mt-0.5" style={{ fontFamily: "'Raleway', sans-serif" }}>{b.l}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Introduction */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <span className="text-amber-600 text-xs font-bold tracking-widest uppercase" style={{ fontFamily: "'Raleway', sans-serif" }}>
              Why Choose a Conservatory?
            </span>
            <h2 className="text-4xl font-bold text-zinc-900 mt-3 mb-5" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Add a Beautiful New Room to Your Surrey Home
            </h2>
            <div className="space-y-4 text-zinc-500 text-sm leading-relaxed" style={{ fontFamily: "'Raleway', sans-serif" }}>
              <p>A conservatory is one of the most popular ways to add both living space and real estate value to a home in Surrey. Whether you want a light-filled dining room, a quiet home office, a children&apos;s playroom, or a social kitchen-diner, there is a conservatory style designed specifically for your needs.</p>
              <p>We install all 18 conservatory styles — from a simple lean-to for a bungalow to a premium bespoke orangery with roof lantern. Every conservatory we build in Caterham and across Surrey is manufactured to your exact measurements and installed by our experienced in-house team.</p>
              <p>Every project starts with a <strong>free home survey</strong> — we measure up, assess your garden and planning position, and provide a detailed written quotation with no hidden extras.</p>
            </div>
            <div className="grid grid-cols-2 gap-4 mt-7">
              {[
                { icon: "ri-tree-line", text: "Brings garden into your home" },
                { icon: "ri-sun-line", text: "Year-round light & warmth" },
                { icon: "ri-home-line", text: "Adds significant property value" },
                { icon: "ri-settings-line", text: "Fully bespoke to your home" },
              ].map((f) => (
                <div key={f.text} className="flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-amber-50 rounded-lg flex-shrink-0">
                    <i className={`${f.icon} text-amber-600 text-base`} />
                  </div>
                  <span className="text-zinc-600 text-xs leading-snug pt-1.5" style={{ fontFamily: "'Raleway', sans-serif" }}>{f.text}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="space-y-4">
            <div className="w-full h-64 rounded-lg overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=elegant%20orangery%20interior%20uk%20home%20comfortable%20furniture%20kitchen%20diner%20glass%20roof%20lantern%20natural%20light%20warm%20inviting%20garden%20view&width=700&height=256&seq=cons-intro1&orientation=landscape"
                alt="Orangery interior Surrey"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="w-full h-40 rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Victorian%20conservatory%20exterior%20uk%20home%20brick%20garden%20summer%20traditional%20elegant%20glass%20roof%20bay%20front&width=350&height=160&seq=cons-intro2&orientation=landscape"
                  alt="Victorian conservatory exterior"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="w-full h-40 rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Edwardian%20conservatory%20interior%20bright%20light%20furnished%20sofa%20table%20family%20space%20comfortable%20uk%20home%20garden&width=350&height=160&seq=cons-intro3&orientation=landscape"
                  alt="Edwardian conservatory interior"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ALL STYLES GRID */}
      <section id="styles" className="py-20 bg-stone-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <span className="text-amber-600 text-xs font-bold tracking-widest uppercase" style={{ fontFamily: "'Raleway', sans-serif" }}>Browse All Designs</span>
            <h2 className="text-4xl font-bold text-zinc-900 mt-3 mb-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              18 Conservatory Styles Available
            </h2>
            <p className="text-zinc-500 text-sm" style={{ fontFamily: "'Raleway', sans-serif" }}>
              Click any style to see detailed information, pricing guidance, and ratings. Then request a free quote directly for that style.
            </p>
          </div>

          {/* Category Filters */}
          <div className="flex flex-wrap gap-2 justify-center mb-8">
            {conservatoryCategories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => { setActiveCategory(cat.id); setSelectedStyle(null); }}
                className={`px-5 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-all cursor-pointer ${
                  activeCategory === cat.id
                    ? "bg-amber-600 text-white"
                    : "bg-white border border-stone-200 text-zinc-600 hover:border-amber-300"
                }`}
                style={{ fontFamily: "'Raleway', sans-serif" }}
              >
                {cat.label}
              </button>
            ))}
          </div>

          {/* Grid + Detail Panel */}
          <div className={`grid gap-6 ${selectedStyle ? "lg:grid-cols-3" : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"}`}>
            {/* Cards */}
            <div className={`${selectedStyle ? "col-span-2" : ""} grid gap-5 ${selectedStyle ? "grid-cols-2 md:grid-cols-2" : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"}`}>
              {filtered.map((style) => (
                <StyleCard
                  key={style.id}
                  style={style}
                  selected={selectedStyle?.id === style.id}
                  onSelect={handleSelectStyle}
                />
              ))}
            </div>
            {/* Detail panel */}
            {selectedStyle && (
              <div className="lg:col-span-1 lg:sticky lg:top-28 h-fit">
                <StyleDetailPanel
                  style={selectedStyle}
                  onQuote={handleQuoteForStyle}
                  onClose={() => setSelectedStyle(null)}
                />
              </div>
            )}
          </div>
        </div>
      </section>

      {/* PRICING GUIDANCE */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="text-amber-600 text-xs font-bold tracking-widest uppercase" style={{ fontFamily: "'Raleway', sans-serif" }}>Pricing Guidance</span>
            <h2 className="text-4xl font-bold text-zinc-900 mt-3 mb-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Conservatory Price Guide for Surrey Homes
            </h2>
            <p className="text-zinc-500 text-sm" style={{ fontFamily: "'Raleway', sans-serif" }}>
              All prices below are for supply and installation inclusive of VAT. Exact pricing depends on size, specification and groundworks. Contact us for a free written quote.
            </p>
          </div>

          <div className="overflow-hidden rounded-xl border border-stone-100">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-zinc-900 text-white">
                    <th className="text-left px-6 py-4 font-semibold" style={{ fontFamily: "'Raleway', sans-serif" }}>Style</th>
                    <th className="text-center px-4 py-4 font-semibold" style={{ fontFamily: "'Raleway', sans-serif" }}>Price Range</th>
                    <th className="text-center px-4 py-4 font-semibold hidden md:table-cell" style={{ fontFamily: "'Raleway', sans-serif" }}>Typical Size</th>
                    <th className="text-center px-4 py-4 font-semibold hidden lg:table-cell" style={{ fontFamily: "'Raleway', sans-serif" }}>Planning</th>
                    <th className="text-center px-4 py-4 font-semibold hidden lg:table-cell" style={{ fontFamily: "'Raleway', sans-serif" }}>Best For</th>
                    <th className="text-center px-4 py-4" />
                  </tr>
                </thead>
                <tbody>
                  {[
                    { name: "Lean-To", range: "£7,500 – £18,000", size: "2.5m × 3m+", planning: "Usually not required", best: "Bungalows, modern homes", highlight: false },
                    { name: "3×3 Conservatory", range: "£8,500 – £20,000", size: "3m × 3m", planning: "Usually not required", best: "Most homes — great starter size", highlight: true },
                    { name: "Edwardian", range: "£9,000 – £22,000", size: "3m – 5m wide", planning: "Usually not required", best: "Maximum floor space", highlight: false },
                    { name: "Victorian", range: "£10,000 – £24,000", size: "3m – 5m wide", planning: "Usually not required", best: "Traditional homes", highlight: false },
                    { name: "Hipped-Back Edwardian", range: "£11,000 – £25,000", size: "3.5m – 5m wide", planning: "Usually not required", best: "Period properties, corners", highlight: false },
                    { name: "Dwarf-Wall", range: "£10,500 – £25,000", size: "3m – 5m", planning: "Usually not required", best: "Privacy, traditional look", highlight: false },
                    { name: "Hipped-Back Victorian", range: "£12,000 – £26,000", size: "3m – 5m wide", planning: "Usually not required", best: "Corner plots", highlight: false },
                    { name: "Gable", range: "£12,500 – £28,000", size: "4m – 6m wide", planning: "Check with us", best: "Maximum light, south-facing", highlight: false },
                    { name: "Hipped-Back Gable", range: "£14,000 – £30,000", size: "4m – 6m wide", planning: "Check with us", best: "Balanced, premium look", highlight: false },
                    { name: "Large (20m²+)", range: "£16,000 – £40,000+", size: "4m × 5m+", planning: "Likely required", best: "Open-plan kitchen-diners", highlight: false },
                    { name: "P-Shape", range: "£15,000 – £38,000", size: "5m – 8m combined", planning: "Check with us", best: "Two spaces in one", highlight: false },
                    { name: "L-Shape", range: "£18,000 – £45,000", size: "Bespoke", planning: "Check with us", best: "Corner plots, two aspects", highlight: false },
                    { name: "Orangery", range: "£20,000 – £50,000+", size: "4m × 5m+", planning: "Varies", best: "Premium kitchen extensions", highlight: true },
                    { name: "Hipped-Back Orangery", range: "£22,000 – £55,000+", size: "4m × 5m+", planning: "Varies", best: "Premium period properties", highlight: false },
                    { name: "Bespoke", range: "£20,000 – £70,000+", size: "Any", planning: "We handle this", best: "Unique projects, no limits", highlight: false },
                  ].map((row, i) => (
                    <tr key={row.name} className={`border-t border-stone-100 ${row.highlight ? "bg-amber-50" : i % 2 === 0 ? "bg-white" : "bg-stone-50/50"}`}>
                      <td className="px-6 py-4">
                        <span className="font-semibold text-zinc-800" style={{ fontFamily: "'Raleway', sans-serif" }}>{row.name}</span>
                        {row.highlight && <span className="ml-2 text-xs bg-amber-600 text-white px-2 py-0.5 rounded-full font-semibold">Popular</span>}
                      </td>
                      <td className="px-4 py-4 text-center text-zinc-700 font-semibold" style={{ fontFamily: "'Raleway', sans-serif" }}>{row.range}</td>
                      <td className="px-4 py-4 text-center text-zinc-500 hidden md:table-cell" style={{ fontFamily: "'Raleway', sans-serif" }}>{row.size}</td>
                      <td className="px-4 py-4 text-center hidden lg:table-cell" style={{ fontFamily: "'Raleway', sans-serif" }}>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${row.planning === "Usually not required" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                          {row.planning}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-center text-zinc-500 text-xs hidden lg:table-cell" style={{ fontFamily: "'Raleway', sans-serif" }}>{row.best}</td>
                      <td className="px-4 py-4 text-center">
                        <button
                          onClick={() => handleQuoteForStyle(row.name)}
                          className="text-xs bg-amber-50 hover:bg-amber-600 text-amber-700 hover:text-white font-semibold px-3 py-1.5 rounded-md transition-colors whitespace-nowrap cursor-pointer"
                          style={{ fontFamily: "'Raleway', sans-serif" }}
                        >
                          Get Quote
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <p className="text-center text-xs text-zinc-400 mt-4" style={{ fontFamily: "'Raleway', sans-serif" }}>
            Prices include VAT and full installation. Groundworks, planning fees, and electrical work may be additional. Free written quotation provided after survey.
          </p>
        </div>
      </section>

      {/* SIDE BY SIDE COMPARISON */}
      <section className="py-20 bg-stone-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="text-amber-600 text-xs font-bold tracking-widest uppercase" style={{ fontFamily: "'Raleway', sans-serif" }}>Style Comparison</span>
            <h2 className="text-4xl font-bold text-zinc-900 mt-3 mb-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Most Popular Styles Side by Side
            </h2>
            <p className="text-zinc-500 text-sm" style={{ fontFamily: "'Raleway', sans-serif" }}>
              Not sure which style is right for you? Here&apos;s how our three most popular designs compare.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {conservatoryStyles.filter((s) => ["lean-to", "edwardian", "victorian"].includes(s.id)).map((style) => (
              <article key={style.id} className="bg-white rounded-xl overflow-hidden border border-stone-100">
                <div className="w-full h-52 overflow-hidden">
                  <img src={style.image} alt={style.name} className="w-full h-full object-cover object-top" />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-zinc-800 mb-1" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{style.name}</h3>
                  <p className="text-amber-600 text-xs font-semibold mb-3" style={{ fontFamily: "'Raleway', sans-serif" }}>{style.tagline}</p>
                  <p className="text-zinc-500 text-xs leading-relaxed mb-4" style={{ fontFamily: "'Raleway', sans-serif" }}>{style.description}</p>
                  <div className="space-y-2 mb-5">
                    {[
                      { label: "Floor Space", val: style.spaceRating },
                      { label: "Natural Light", val: style.lightRating },
                      { label: "Value for Money", val: style.budgetRating },
                    ].map((r) => (
                      <div key={r.label} className="flex items-center justify-between">
                        <span className="text-xs text-zinc-400" style={{ fontFamily: "'Raleway', sans-serif" }}>{r.label}</span>
                        <div className="flex gap-1">
                          {Array.from({ length: 5 }).map((_, i) => (
                            <span key={i} className={`w-3 h-3 rounded-sm ${i < r.val ? "bg-amber-500" : "bg-stone-200"}`} />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="pt-4 border-t border-stone-100 flex items-center justify-between">
                    <span className="text-lg font-bold text-zinc-800" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{style.priceFrom}</span>
                    <button
                      onClick={() => handleQuoteForStyle(style.name)}
                      className="text-xs bg-amber-600 hover:bg-amber-500 text-white font-semibold px-4 py-2 rounded-md transition-colors whitespace-nowrap cursor-pointer"
                      style={{ fontFamily: "'Raleway', sans-serif" }}
                    >
                      Get Quote
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Features & Benefits */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <div>
              <span className="text-amber-600 text-xs font-bold tracking-widest uppercase" style={{ fontFamily: "'Raleway', sans-serif" }}>Why Ensure Windows?</span>
              <h2 className="text-4xl font-bold text-zinc-900 mt-3 mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Every Conservatory Installed by Our In-House Team
              </h2>
              <div className="space-y-5">
                {[
                  { icon: "ri-shield-check-line", title: "10-Year Installation Guarantee", desc: "All our conservatories come with a full 10-year guarantee covering frames, glazing, and weathertightness." },
                  { icon: "ri-ruler-2-line", title: "Made to Measure", desc: "Every conservatory is manufactured to your exact opening dimensions. No off-the-shelf compromises." },
                  { icon: "ri-sun-line", title: "Solar Control Glazing", desc: "We recommend and fit solar-control self-cleaning glass as standard on all roof panels and overhead glazing." },
                  { icon: "ri-file-text-line", title: "We Handle Planning", desc: "We check your permitted development position and can submit any planning applications required, on your behalf." },
                  { icon: "ri-home-gear-line", title: "Full Base & Groundworks", desc: "We manage the complete project from base slab and DPC through to final installation and decoration." },
                  { icon: "ri-map-pin-2-line", title: "Local to Caterham, Surrey", desc: "Based in Caterham with an established local reputation. We know Surrey planning and building regs inside out." },
                ].map((f) => (
                  <div key={f.title} className="flex gap-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-amber-50 rounded-lg flex-shrink-0">
                      <i className={`${f.icon} text-amber-600`} />
                    </div>
                    <div>
                      <h3 className="font-bold text-zinc-800 text-sm mb-1" style={{ fontFamily: "'Raleway', sans-serif" }}>{f.title}</h3>
                      <p className="text-zinc-500 text-sm leading-relaxed" style={{ fontFamily: "'Raleway', sans-serif" }}>{f.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-4">
              <div className="w-full h-72 rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=conservatory%20installation%20team%20working%20fitting%20aluminium%20frame%20glazing%20uk%20professionals%20skilled%20tradesmen%20quality%20workmanship&width=700&height=290&seq=cons-team&orientation=landscape"
                  alt="Conservatory installation team Surrey"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="bg-amber-600 rounded-lg p-7 text-white">
                <h3 className="text-2xl font-bold mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Areas We Cover</h3>
                <p className="text-amber-100 text-sm mb-4" style={{ fontFamily: "'Raleway', sans-serif" }}>We install conservatories across Surrey and South London, including:</p>
                <div className="grid grid-cols-2 gap-1.5">
                  {["Caterham", "Redhill", "Reigate", "Oxted", "Warlingham", "Purley", "Coulsdon", "Croydon", "Banstead", "Horley", "Merstham", "Godstone"].map((a) => (
                    <div key={a} className="flex items-center gap-1.5">
                      <i className="ri-map-pin-2-line text-amber-200 text-xs" />
                      <span className="text-amber-100 text-xs" style={{ fontFamily: "'Raleway', sans-serif" }}>{a}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 bg-stone-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="text-amber-600 text-xs font-bold tracking-widest uppercase" style={{ fontFamily: "'Raleway', sans-serif" }}>How It Works</span>
            <h2 className="text-4xl font-bold text-zinc-900 mt-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Our 4-Step Process</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {processSteps.map((s, i) => (
              <div key={s.step} className="relative text-center bg-white p-7 rounded-xl border border-stone-100">
                {i < processSteps.length - 1 && (
                  <div className="hidden lg:block absolute top-12 left-[calc(50%+2.5rem)] right-0 border-t-2 border-dashed border-amber-200 z-0" />
                )}
                <div className="relative z-10 w-14 h-14 flex items-center justify-center bg-amber-600 rounded-full mx-auto mb-5 text-white text-xl font-bold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  {s.step}
                </div>
                <h3 className="text-base font-bold text-zinc-800 mb-2" style={{ fontFamily: "'Raleway', sans-serif" }}>{s.title}</h3>
                <p className="text-zinc-500 text-sm leading-relaxed" style={{ fontFamily: "'Raleway', sans-serif" }}>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="text-amber-600 text-xs font-bold tracking-widest uppercase" style={{ fontFamily: "'Raleway', sans-serif" }}>Our Work</span>
            <h2 className="text-4xl font-bold text-zinc-900 mt-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Conservatory Installations Across Surrey</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {[
              { src: "https://readdy.ai/api/search-image?query=Victorian%20conservatory%20exterior%20red%20brick%20uk%20home%20garden%20elegant%20ornate%20bay%20front%20traditional%20well%20maintained%20spring%20flowers&width=600&height=400&seq=cg1&orientation=landscape", alt: "Victorian conservatory, Caterham" },
              { src: "https://readdy.ai/api/search-image?query=orangery%20interior%20uk%20home%20luxury%20glass%20roof%20lantern%20dining%20table%20comfortable%20bright%20beautiful%20garden%20view%20natural%20light&width=600&height=400&seq=cg2&orientation=landscape", alt: "Orangery interior, Redhill" },
              { src: "https://readdy.ai/api/search-image?query=lean%20to%20conservatory%20modern%20aluminium%20white%20bungalow%20uk%20home%20neat%20garden%20small%20compact%20clean%20simple%20attached&width=600&height=400&seq=cg3&orientation=landscape", alt: "Lean-to conservatory, Oxted" },
              { src: "https://readdy.ai/api/search-image?query=Edwardian%20conservatory%20bright%20sunny%20interior%20white%20uPVC%20living%20room%20furniture%20comfortable%20uk%20home%20rear%20extension&width=600&height=400&seq=cg4&orientation=landscape", alt: "Edwardian conservatory, Warlingham" },
              { src: "https://readdy.ai/api/search-image?query=gable%20conservatory%20tall%20dramatic%20glass%20front%20uk%20home%20bright%20ceiling%20light%20airy%20spacious%20interior%20vaulted&width=600&height=400&seq=cg5&orientation=landscape", alt: "Gable conservatory, Purley" },
              { src: "https://readdy.ai/api/search-image?query=large%20P%20shape%20conservatory%20uk%20home%20two%20sections%20lean%20to%20Victorian%20combined%20large%20garden%20patio%20view%20summer&width=600&height=400&seq=cg6&orientation=landscape", alt: "P-shape conservatory, Coulsdon" },
            ].map((img) => (
              <div key={img.src} className="w-full h-56 md:h-64 rounded-lg overflow-hidden group cursor-pointer">
                <img src={img.src} alt={img.alt} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/gallery" className="inline-flex items-center gap-2 text-amber-600 font-semibold hover:gap-3 transition-all cursor-pointer" style={{ fontFamily: "'Raleway', sans-serif" }}>
              View Full Gallery <div className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-line" /></div>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-stone-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="text-amber-600 text-xs font-bold tracking-widest uppercase" style={{ fontFamily: "'Raleway', sans-serif" }}>Customer Reviews</span>
            <h2 className="text-4xl font-bold text-zinc-900 mt-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>What Our Conservatory Customers Say</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((t) => (
              <div key={t.name} className="bg-white rounded-xl p-7 border border-stone-100">
                <div className="flex gap-1 mb-1">{[1,2,3,4,5].map((i) => <div key={i} className="w-4 h-4 flex items-center justify-center"><i className="ri-star-fill text-amber-500 text-sm" /></div>)}</div>
                <span className="inline-block text-xs text-amber-600 font-semibold mb-3" style={{ fontFamily: "'Raleway', sans-serif" }}>{t.style} conservatory</span>
                <p className="text-zinc-600 text-sm leading-relaxed mb-6 italic" style={{ fontFamily: "'Raleway', sans-serif" }}>&ldquo;{t.text}&rdquo;</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-amber-600 rounded-full text-white text-xs font-bold flex-shrink-0">{t.avatar}</div>
                  <div>
                    <p className="text-zinc-800 text-sm font-semibold" style={{ fontFamily: "'Raleway', sans-serif" }}>{t.name}</p>
                    <p className="text-zinc-400 text-xs" style={{ fontFamily: "'Raleway', sans-serif" }}>{t.location}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="text-amber-600 text-xs font-bold tracking-widest uppercase" style={{ fontFamily: "'Raleway', sans-serif" }}>FAQ</span>
            <h2 className="text-4xl font-bold text-zinc-900 mt-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Frequently Asked Questions</h2>
          </div>
          <div className="space-y-3">
            {faqItems.map((faq, i) => (
              <div key={i} className="border border-stone-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-6 py-4 text-left bg-white hover:bg-stone-50 transition-colors cursor-pointer"
                >
                  <span className="font-semibold text-zinc-800 text-sm pr-4" style={{ fontFamily: "'Raleway', sans-serif" }}>{faq.q}</span>
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                    <i className={`${openFaq === i ? "ri-subtract-line" : "ri-add-line"} text-amber-600`} />
                  </div>
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-5 bg-stone-50">
                    <p className="text-zinc-500 text-sm leading-relaxed" style={{ fontFamily: "'Raleway', sans-serif" }}>{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quote Form */}
      <section id="quote-form" ref={quoteRef} className="py-20" style={{ background: "linear-gradient(135deg, #1a1a1a 0%, #2d1f0e 100%)" }}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-14 items-start">
            <div>
              <span className="text-amber-400 text-xs font-bold tracking-widest uppercase" style={{ fontFamily: "'Raleway', sans-serif" }}>Get Started</span>
              <h2 className="text-4xl font-bold text-white mt-4 mb-5" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Get Your Free Conservatory Quote
              </h2>
              <p className="text-stone-300 text-sm leading-relaxed mb-8" style={{ fontFamily: "'Raleway', sans-serif" }}>
                Tell us which style interests you and we will arrange a free, no-obligation home survey and provide a detailed written quotation within 48 hours. No pushy sales, just honest expert advice.
              </p>
              {quoteStyle && (
                <div className="bg-amber-600/20 border border-amber-600/40 rounded-lg px-5 py-4 mb-6">
                  <div className="flex items-center gap-2">
                    <i className="ri-check-circle-line text-amber-400" />
                    <span className="text-amber-300 text-sm font-semibold" style={{ fontFamily: "'Raleway', sans-serif" }}>
                      Enquiring about: {quoteStyle}
                    </span>
                  </div>
                </div>
              )}
              <div className="flex flex-col gap-4">
                {[
                  { icon: "ri-phone-line", text: "07852 636 300", href: "tel:07852636300" },
                  { icon: "ri-mail-line", text: "sales@ensurewindows.co.uk", href: "mailto:sales@ensurewindows.co.uk" },
                  { icon: "ri-map-pin-2-line", text: "Seven House, Town End, Caterham CR3 5UG", href: "#" },
                ].map((item) => (
                  <a key={item.text} href={item.href} className="flex items-center gap-3 cursor-pointer group">
                    <div className="w-9 h-9 flex items-center justify-center bg-amber-600/20 rounded-md flex-shrink-0">
                      <i className={`${item.icon} text-amber-400`} />
                    </div>
                    <span className="text-stone-300 group-hover:text-amber-400 transition-colors text-sm" style={{ fontFamily: "'Raleway', sans-serif" }}>{item.text}</span>
                  </a>
                ))}
              </div>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-xl p-8">
              {submitted ? (
                <div className="text-center py-10">
                  <div className="w-16 h-16 flex items-center justify-center bg-amber-600/20 rounded-full mx-auto mb-4">
                    <i className="ri-check-line text-amber-400 text-3xl" />
                  </div>
                  <h3 className="text-white text-xl font-bold mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Thank You!</h3>
                  <p className="text-stone-300 text-sm" style={{ fontFamily: "'Raleway', sans-serif" }}>We&apos;ll be in touch within 24 hours to arrange your free survey.</p>
                </div>
              ) : (
                <>
                  <h3 className="text-white text-xl font-bold mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Request a Free Survey &amp; Quote</h3>
                  <form onSubmit={handleSubmit} data-readdy-form className="space-y-4">
                    <input
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      required
                      placeholder="Your Full Name"
                      className="w-full bg-white/10 border border-white/20 text-white placeholder-stone-400 text-sm px-4 py-3 rounded-md focus:outline-none focus:border-amber-400"
                      style={{ fontFamily: "'Raleway', sans-serif" }}
                    />
                    <input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      required
                      placeholder="Email Address"
                      className="w-full bg-white/10 border border-white/20 text-white placeholder-stone-400 text-sm px-4 py-3 rounded-md focus:outline-none focus:border-amber-400"
                      style={{ fontFamily: "'Raleway', sans-serif" }}
                    />
                    <input
                      name="phone"
                      value={form.phone}
                      onChange={handleChange}
                      placeholder="Phone Number"
                      className="w-full bg-white/10 border border-white/20 text-white placeholder-stone-400 text-sm px-4 py-3 rounded-md focus:outline-none focus:border-amber-400"
                      style={{ fontFamily: "'Raleway', sans-serif" }}
                    />
                    <select
                      name="style"
                      value={form.style}
                      onChange={handleChange}
                      className="w-full bg-white/10 border border-white/20 text-stone-300 text-sm px-4 py-3 rounded-md focus:outline-none focus:border-amber-400"
                      style={{ fontFamily: "'Raleway', sans-serif" }}
                    >
                      <option value="">Select a conservatory style</option>
                      {conservatoryStyles.map((s) => (
                        <option key={s.id} value={s.name}>{s.name}</option>
                      ))}
                      <option value="Not sure yet">Not sure yet — need advice</option>
                    </select>
                    <select
                      name="size"
                      value={form.size}
                      onChange={handleChange}
                      className="w-full bg-white/10 border border-white/20 text-stone-300 text-sm px-4 py-3 rounded-md focus:outline-none focus:border-amber-400"
                      style={{ fontFamily: "'Raleway', sans-serif" }}
                    >
                      <option value="">Approximate size (if known)</option>
                      <option>Under 3m × 3m</option>
                      <option>3m × 3m (standard)</option>
                      <option>3m × 4m</option>
                      <option>4m × 4m</option>
                      <option>4m × 5m or larger</option>
                      <option>Not sure yet</option>
                    </select>
                    <textarea
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      rows={3}
                      maxLength={500}
                      placeholder="Tell us about your project, any planning considerations, or questions..."
                      className="w-full bg-white/10 border border-white/20 text-white placeholder-stone-400 text-sm px-4 py-3 rounded-md focus:outline-none focus:border-amber-400 resize-none"
                      style={{ fontFamily: "'Raleway', sans-serif" }}
                    />
                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full bg-amber-600 hover:bg-amber-500 text-white font-semibold py-3.5 rounded-md transition-colors whitespace-nowrap cursor-pointer"
                      style={{ fontFamily: "'Raleway', sans-serif" }}
                    >
                      {submitting ? "Sending..." : "Request Free Survey & Quote"}
                    </button>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
